import type { FC } from "react";
import { useMemo } from "react";
import { router } from "expo-router";
import {
  Card,
  ChartContainer,
  ChartMentalBalance,
  computeRadiusByValue,
  getIndexes,
  getValue,
  RenderLabel,
  RenderValue,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Pie, PieChart, Sector } from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { useMedia } from "tamagui";

import { api } from "~/utils/api";
import { normalizeMentalBalanceSpheres } from "~/utils/query-formatters";
import { MentalBalanceMobilePieChart } from "./mental-balance-mobile";

interface MentalBalanceProps {
  disabledLabelLink?: boolean;
}

export const MentalBalance: FC<MentalBalanceProps> = ({
  disabledLabelLink = false,
}) => {
  const { gtMd } = useMedia();
  const { t } = useTranslation("results");
  const [data] = api.scores.spheres.useSuspenseQuery(
    {},
    {
      select: (data) =>
        normalizeMentalBalanceSpheres(data, ChartMentalBalance.innerSectors),
    },
  );

  return (
    <Card
      data-id="mental-balance-card"
      gap={"$4"}
      shadow={gtMd ? "md" : "none"}
      variant={gtMd ? "default" : "borderless"}
      size={gtMd ? "md" : "sm"}
      borderRadius={gtMd ? undefined : "$2"}
      backgroundColor={gtMd ? undefined : "$warm-snow-500"}
    >
      <Card.Title
        data-id={"mental-balance-card-title"}
        textAlign={gtMd ? "center" : "left"}
      >
        {t("mentalBalanceCard.title")}
      </Card.Title>
      <Card.RenderMd md={t("mentalBalanceCard.details")} />

      {gtMd ? (
        <Card
          data-id="mental-balance-card-chart"
          alignItems={"center"}
          variant="borderless"
          paddingHorizontal={"$2"}
          backgroundColor={"$gray-50"}
        >
          <MentalBalancePieChart
            data={data}
            disabledLabelLink={disabledLabelLink}
          />
        </Card>
      ) : (
        <MentalBalanceMobilePieChart
          data={data}
          disabledLabelLink={disabledLabelLink}
        />
      )}
    </Card>
  );
};

const MentalBalancePieChart: FC<{
  data: ReturnType<typeof normalizeMentalBalanceSpheres>;
  disabledLabelLink?: boolean;
}> = ({ data, disabledLabelLink }) => {
  const handleLabelClick = useMemo(() => {
    if (disabledLabelLink) return undefined;
    else return (name: string) => router.navigate("/results/" + name);
  }, [disabledLabelLink]);

  return (
    <ChartContainer
      data-id="mental-balance-chart-container"
      width="100%"
      maxWidth={800}
      height={600}
      config={ChartMentalBalance.config}
    >
      {({ width, height, widthScale }) => {
        const activeIndex = getIndexes(data);
        const outerRadius = Math.floor(width / 6);
        const innerRadius = Math.floor(outerRadius / 8);
        const activeShapeLabel = (props: PieSectorDataItem) => {
          return (
            <RenderLabel
              layout={"lg"}
              data={data}
              domain={props.name}
              widthScale={widthScale}
              {...props}
              containerWidth={width}
              containerHeight={height}
              onLabelClick={handleLabelClick}
            />
          );
        };

        const activeShapeChart = (props: PieSectorDataItem) => {
          const { cx, cy, midAngle, name } = props;

          const dynamicOuterRadius = computeRadiusByValue(
            getValue("domain", name, data)?.value,
            outerRadius,
          );

          return (
            <>
              <Sector {...props} outerRadius={dynamicOuterRadius} />
              <RenderValue
                cx={cx ?? 0}
                cy={cy ?? 0}
                innerRadius={innerRadius}
                midAngle={midAngle ?? 0}
                domain={name}
                value={getValue("domain", name, data)?.value}
                dynamicOuterRadius={dynamicOuterRadius}
              />
            </>
          );
        };

        return (
          <PieChart>
            <Pie
              data={ChartMentalBalance.outerSectors}
              dataKey={"slice"}
              nameKey={"domain"}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              cx="50%"
              cy="50%"
              strokeWidth={0}
              isAnimationActive={false}
              activeIndex={activeIndex}
              activeShape={activeShapeLabel}
            />

            <Pie
              isAnimationActive={false}
              activeIndex={activeIndex}
              activeShape={activeShapeChart}
              strokeWidth={0}
              innerRadius={innerRadius}
              data={data}
              dataKey="slice"
              nameKey="domain"
              outerRadius={0}
              cx="50%"
              cy="50%"
            />
          </PieChart>
        );
      }}
    </ChartContainer>
  );
};
